import { useEffect, useState } from "react";
import axiosClient from "../api/axiosClient";
import { useNavigate } from "react-router-dom";
import PriceBreakdown from "../components/PriceBreakdown";
import BookingTimeline from "../components/BookingTimeline";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import LoadingSpinner from "../components/LoadingSpinner";
import "./MyBookings.css";

/* ── bike image helper ── */
const getBikeImage = (img) => {
  if (!img) return null;
  const c = img.trim();
  if (c.startsWith("http://") || c.startsWith("https://")) return c;
  return `https://bros-bike-rental-api.onrender.com/${c.replace(/^\/+/, "")}`;
};

function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, token } = useAuth();

  useEffect(() => {
    if (!user || !token) { setLoading(false); return; }

    axiosClient
      .get("/bookings/my", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { setBookings(res.data); setLoading(false); })
      .catch((err) => { console.error("Error fetching bookings", err); setLoading(false); });
  }, [user, token]);

  const cancelBooking = async (bookingId) => {
    const result = await Swal.fire({
      title: "Cancel Booking?",
      text: "Are you sure you want to cancel this booking?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, cancel it!",
    });
    if (!result.isConfirmed) return;

    try {
      await axiosClient.put(
        `/bookings/cancel/${bookingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Booking cancelled successfully");
      setBookings((prev) =>
        prev.map((b) =>
          b._id === bookingId
            ? { ...b, bookingStatus: "cancelled", cancellationReason: "user_cancelled" }
            : b
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to cancel booking");
    }
  };

  if (!user) return (
    <div className="my-bookings-page">
      <div className="bookings-header"><h2>Please login to view your bookings</h2></div>
    </div>
  );
  if (loading) return (
    <div className="my-bookings-page">
      <LoadingSpinner message="Loading your bookings" sub="Fetching your ride history…" />
    </div>
  );

  return (
    <div className="my-bookings-page">
      <div className="bookings-header">
        <h2>My Bookings</h2>
        <p className="bookings-sub">All your ride history in one place</p>
      </div>

      {bookings.length === 0 && (
        <div className="empty-state">
          <span className="empty-icon">🛵</span>
          <p>No bookings found yet.</p>
          <button className="empty-cta" onClick={() => navigate("/")}>
            Browse Bikes →
          </button>
        </div>
      )}

      <div className="bookings-list">
        {bookings.map((b, index) => {
          const isPaymentTimeout = b.cancellationReason === "payment_timeout";
          const isUserCancelled = b.bookingStatus === "cancelled" && !isPaymentTimeout;
          const bikeImg = getBikeImage(b.bikeId?.image);

          return (
            <div
              key={b._id}
              className={`booking-card${isPaymentTimeout ? " booking-card--expired" : ""}`}
              style={{ animationDelay: `${index * 60}ms` }}
            >
              {/* ── Payment Timeout Banner ── */}
              {isPaymentTimeout && (
                <div className="expired-banner">
                  <span className="expired-banner-icon">⏱</span>
                  <div className="expired-banner-text">
                    <strong>Payment window expired</strong>
                    <span>
                      This booking was automatically cancelled because payment wasn't
                      completed within 2 minutes. The bike is now available again.
                    </span>
                  </div>
                </div>
              )}

              <div className="booking-top-row">
                <div className="booking-bike-info">
                  {bikeImg && (
                    <img src={bikeImg} alt={b.bikeId?.name} className="booking-bike-thumb" />
                  )}
                  <div>
                    <h3 className="booking-bike-name">{b.bikeId?.name}</h3>
                    <span className="booking-bike-type">{b.bikeId?.type}</span>
                  </div>
                </div>

                {/* Status badge */}
                {isPaymentTimeout ? (
                  <div className="status-badge status-expired">
                    Booking Cancelled
                  </div>
                ) : (
                  <div className={`status-badge status-${b.bookingStatus}`}>
                    {b.bookingStatus}
                  </div>
                )}
              </div>

              <div className="booking-details-grid">
                <div className="detail-item">
                  <span className="detail-label">Pickup</span>
                  <span className="detail-value">{new Date(b.startDate).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Drop-off</span>
                  <span className="detail-value">{new Date(b.endDate).toLocaleString()}</span>
                </div>
              </div>

              <PriceBreakdown
                pricePerDay={b.bikeId?.pricePerDay}
                startDate={b.startDate}
                endDate={b.endDate}
              />

              <div className="booking-total-row">
                <div className="detail-item">
                  <span className="detail-label">Payment Status</span>
                  <span className={`payment-badge payment-${b.paymentStatus === "paid" ? "paid" : "unpaid"}`}>
                    {b.paymentStatus.toUpperCase()}
                  </span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span className="total-label">Total: </span>
                  <span className="total-price">₹{b.totalAmount}</span>
                </div>
              </div>

              <BookingTimeline booking={b} />

              {/* User review */}
              {b.review && (
                <div className="review-section">
                  <span className="detail-label">Your Review</span>
                  <div className="review-text">
                    {b.review.rating} ⭐ — "{b.review.comment}"
                  </div>
                </div>
              )}

              {/* Pending-booking hint */}
              {b.bookingStatus === "pending" && !isPaymentTimeout && (
                <p style={{ fontSize: "13px", color: "var(--muted)", margin: "0" }}>
                  You will receive pickup instructions after confirmation.
                </p>
              )}

              <div className="booking-actions">
                {/* ── Rebook CTA for payment-expired bookings ── */}
                {isPaymentTimeout && (
                  <button
                    className="action-btn btn-rebook"
                    onClick={() => navigate(`/booking?bikeId=${b.bikeId?._id}`)}
                  >
                    🔄 Rebook This Bike
                  </button>
                )}


                {b.bookingStatus === "confirmed" && (
                  <button
                    className="action-btn btn-primary"
                    onClick={() => navigate("/pickup-instructions")}
                  >
                    Pickup Instructions
                  </button>
                )}

                {b.paymentStatus !== "paid" && b.bookingStatus !== "cancelled" && (
                  <button
                    className="action-btn btn-danger"
                    onClick={() => cancelBooking(b._id)}
                  >
                    Cancel Booking
                  </button>
                )}

                {b.paymentStatus === "paid" && b.bookingStatus === "confirmed" && (
                  <button
                    className="action-btn btn-warning"
                    onClick={() => navigate("/contact-support", { state: { booking: b } })}
                  >
                    Cancellation & Refund
                  </button>
                )}

                {b.returnStatus && !b.reviewed && (
                  <button
                    onClick={() => navigate(`/review/${b._id}`)}
                    className="action-btn btn-dark"
                  >
                    Give Review ⭐
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MyBookings;
